import { useRef, useState } from 'react';
import { List, Avatar, Button, Popconfirm, Typography, Modal, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const LONG_PRESS_DELAY = 550;
const TOUCH_MOVE_LIMIT = 12;

// 把消息内容里的图片渲染出来：支持 markdown ![alt](url) 与裸图片 URL，其余按文本。
const MD_IMAGE_RE = /!\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/g;
const BARE_IMAGE_RE = /(https?:\/\/[^\s)]+\.(?:png|jpe?g|gif|webp|bmp|svg)(?:\?[^\s)]*)?)/gi;

function renderMessageContent(content, onImage) {
  if (typeof content !== 'string' || !content) return content;

  // 收集所有图片 URL 及其在字符串中的区间
  const segments = [];
  let lastIndex = 0;
  const pushText = (text) => { if (text) segments.push({ type: 'text', value: text }); };

  // 统一用一个正则轮询：先匹配 markdown 图片，再匹配裸图片链接
  const combined = new RegExp(`${MD_IMAGE_RE.source}|${BARE_IMAGE_RE.source}`, 'gi');
  let m;
  while ((m = combined.exec(content)) !== null) {
    pushText(content.slice(lastIndex, m.index));
    const url = m[1] || m[2] || m[0];
    segments.push({ type: 'image', value: url });
    lastIndex = m.index + m[0].length;
  }
  pushText(content.slice(lastIndex));

  if (segments.every(s => s.type === 'text')) return content; // 无图片，原样文本

  return segments.map((s, i) => {
    if (s.type === 'image') {
      return (
        <img
          key={i}
          src={s.value}
          alt="生成图片"
          className="message-image"
          loading="lazy"
          onClick={() => onImage && onImage(s.value)}
        />
      );
    }
    return <span key={i}>{s.value}</span>;
  });
}


function MessageList({ messages, loading, onDelete, assistantName }) {
  const botName = assistantName || '万能的我';
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);

  const openImagePreview = (src) => {
    setImagePreview(src);
  };

  const closeImagePreview = () => {
    setImagePreview(null);
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const fallbackCopyText = (content) => {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const copyMessage = async (content) => {
    if (!content || !content.trim()) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else if (!fallbackCopyText(content)) {
        throw new Error('copy failed');
      }

      message.success('消息已复制');
    } catch {
      try {
        if (fallbackCopyText(content)) {
          message.success('消息已复制');
          return;
        }
      } catch {
        // Fall through to the shared failure prompt.
      }

      message.error('复制失败');
    }
  };

  const startLongPressCopy = (event, content) => {
    if (!content || !content.trim()) return;

    const touch = event.touches?.[0];
    touchStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    clearLongPressTimer();

    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      copyMessage(content);
    }, LONG_PRESS_DELAY);
  };

  const cancelLongPressOnMove = (event) => {
    if (!touchStartRef.current || !longPressTimerRef.current) return;

    const touch = event.touches?.[0];
    if (!touch) return;

    const movedX = Math.abs(touch.clientX - touchStartRef.current.x);
    const movedY = Math.abs(touch.clientY - touchStartRef.current.y);
    if (movedX > TOUCH_MOVE_LIMIT || movedY > TOUCH_MOVE_LIMIT) {
      clearLongPressTimer();
    }
  };

  const stopLongPressCopy = () => {
    clearLongPressTimer();
    touchStartRef.current = null;
  };

  return (
    <>
    <List
      className="wechat-message-list"
      loading={loading}
      dataSource={messages}
      renderItem={msg => {
        const isUser = msg.role === 'user';
        const displayName = isUser ? '我' : botName;

        return (
          <List.Item className={isUser ? 'message-row message-row-user' : 'message-row message-row-assistant'}>
            <div className="message-stack">
              <Avatar className={isUser ? 'message-avatar user-avatar' : 'message-avatar assistant-avatar'}>
                {isUser ? '我' : displayName.slice(0, 1)}
              </Avatar>
              <div className="message-body">
                <div className="message-name">{displayName}</div>
                <div className="message-bubble-wrap">
                  {isUser && onDelete && (
                    <Popconfirm
                      title="删除这条消息？"
                      okText="删除"
                      cancelText="取消"
                      onConfirm={() => onDelete(msg)}
                    >
                      <Button className="message-delete" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                  <div
                    className={isUser ? 'message-bubble user-bubble' : 'message-bubble assistant-bubble'}
                    onTouchCancel={stopLongPressCopy}
                    onTouchEnd={stopLongPressCopy}
                    onTouchMove={cancelLongPressOnMove}
                    onTouchStart={event => startLongPressCopy(event, msg.content)}
                  >
                    {msg.status === 'pending' ? (
                      <span className="typing-indicator" aria-label="正在回复">
                        <span />
                        <span />
                        <span />
                      </span>
                    ) : renderMessageContent(msg.content, openImagePreview)}
                  </div>
                </div>
                {msg.status === 'failed' && <Text type="danger" className="message-status">发送失败</Text>}
              </div>
            </div>
          </List.Item>
        );
      }}
    />
    <Modal
      visible={!!imagePreview}
      footer={null}
      onCancel={closeImagePreview}
      width="90vw"
      bodyStyle={{ padding: 0, textAlign: 'center', background: '#0b1020', borderRadius: 12 }}
      destroyOnClose
    >
      {imagePreview && (
        <img
          src={imagePreview}
          alt="预览"
          style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
        />
      )}
    </Modal>
    </>
  );
}

export default MessageList;
